import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { map, switchMap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FileUploadComponent } from 'src/app/components/file-upload/file-upload.component';
import { FilePathPipe } from 'src/app/shared/pipes/file-path.pipe';
import { User } from 'src/app/models';
import { UserService } from 'src/app/services/user.service';
import { MediaService } from 'src/app/services/media.service';

@Component({
  selector: 'app-image-upload-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    FileUploadComponent,
    FilePathPipe,
  ],
  templateUrl: './image-upload-modal.component.html',
  styles: [`
    .preview-frame {
      width: 160px;
      height: 160px;
      border-radius: 50%;
      overflow: hidden;
      background-color: #f5f5f5;
    }
    .preview-frame img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  `],
})
export class ImageUploadModalComponent {
  readonly dialogRef = inject(MatDialogRef<ImageUploadModalComponent>);
  readonly data = inject<User>(MAT_DIALOG_DATA);
  userId = this.data.id;

  userService = inject(UserService);
  mediaService = inject(MediaService);
  toaster = inject(ToastrService);

  fileControl = new FormControl<File | null>(null, [Validators.required]);
  previewUrl: string | null = null;
  loading = false;

  constructor() {
    this.fileControl.valueChanges.subscribe((file) => {
      this.revokePreview();
      this.previewUrl = file ? URL.createObjectURL(file) : null;
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  submit() {
    const file = this.fileControl.value;
    if (!file || !this.userId || this.loading) return;

    this.loading = true;
    this.mediaService.uploadSingle(file, 'profile').pipe(
      switchMap(({ path }) =>
        this.userService.updateUser(this.userId!, { image: path }).pipe(
          // Surface the persisted path to the caller — the PATCH response
          // is a partial user, so we return only the diff to merge.
          map(() => ({ image: path })),
        ),
      ),
    ).subscribe({
      next: (patch) => {
        this.toaster.success('Profile picture updated');
        this.loading = false;
        this.dialogRef.close(patch);
      },
      error: (err) => {
        this.loading = false;
        this.toaster.error(err?.error?.message || 'Failed to upload image');
      },
    });
  }

  private revokePreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  }

  ngOnDestroy() {
    this.revokePreview();
  }
}
